import { IntlShape } from "react-intl";

const MONTH_I18N_KEYS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

const DEFAULT_MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DEFAULT_MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const padZero = (n: number) => (n < 10 ? `0${n}` : `${n}`);

export type TranslatedDateFormat =
  // e.g. "Aug 3" / "3 Ago" / "8月3日"
  | "MMM-D"
  // e.g. "August 2024" / "agosto 2024" / "2024年8月"
  | "MMMM-YYYY"
  // e.g. "Aug 03 2024" / "03 Ago 2024" / "2024年8月03日"
  | "MMM-DD-YYYY"
  // e.g. "August 15, 2024" / "15 de agosto de 2024"
  | "MMMM-D-YYYY"
  // e.g. "Aug 3, 2024" / "3 Ago 2024"
  | "MMM-D-YYYY";

type DateInput = Date | string | number | null | undefined;

const toDate = (input: DateInput): Date | null => {
  if (!input && input !== 0) return null;
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getMonth = (
  intl: IntlShape | undefined,
  monthIndex: number,
  variant: "long" | "short"
): string => {
  const key = MONTH_I18N_KEYS[monthIndex];
  const fallback =
    variant === "long"
      ? DEFAULT_MONTHS_LONG[monthIndex]
      : DEFAULT_MONTHS_SHORT[monthIndex];
  if (!intl || !key) return fallback;
  return intl.formatMessage({
    id: `common.months.${key}.${variant}`,
    defaultMessage: fallback,
  });
};

/**
 * Format a date using a translated month name and the locale's preferred
 * ordering of day / month / year via the `verbiage.date-*` i18n keys.
 *
 * Falls back to English month names when no intl instance is supplied.
 */
export const formatTranslatedDate = (
  intl: IntlShape | undefined,
  input: DateInput,
  format: TranslatedDateFormat
): string => {
  const date = toDate(input);
  if (!date) return "";

  const monthIndex = date.getMonth();
  const day = date.getDate();
  const year = date.getFullYear();

  switch (format) {
    case "MMM-D": {
      const month = getMonth(intl, monthIndex, "short");
      if (!intl) return `${month} ${day}`;
      return intl.formatMessage(
        {
          id: "verbiage.date-month-day-short",
          defaultMessage: "{month} {day}",
        },
        { month, day }
      );
    }
    case "MMMM-YYYY": {
      const month = getMonth(intl, monthIndex, "long");
      if (!intl) return `${month} ${year}`;
      return intl.formatMessage(
        {
          id: "verbiage.date-month-year-long",
          defaultMessage: "{month} {year}",
        },
        { month, year }
      );
    }
    case "MMM-DD-YYYY": {
      const month = getMonth(intl, monthIndex, "short");
      if (!intl) return `${month} ${padZero(day)} ${year}`;
      return intl.formatMessage(
        {
          id: "verbiage.date-month-day-year-short",
          defaultMessage: "{month} {day} {year}",
        },
        { month, day: padZero(day), year }
      );
    }
    case "MMM-D-YYYY": {
      const month = getMonth(intl, monthIndex, "short");
      if (!intl) return `${month} ${day}, ${year}`;
      return intl.formatMessage(
        {
          id: "verbiage.date-month-day-year-long",
          defaultMessage: "{month} {day}, {year}",
        },
        { month, day, year }
      );
    }
    case "MMMM-D-YYYY": {
      const month = getMonth(intl, monthIndex, "long");
      if (!intl) return `${month} ${day}, ${year}`;
      return intl.formatMessage(
        {
          id: "verbiage.date-month-day-year-long",
          defaultMessage: "{month} {day}, {year}",
        },
        { month, day, year }
      );
    }
    default:
      return "";
  }
};
